import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { useDashboard, useCompleteTask, useUpload } from "@/api/hooks";
import TaskCard from "@/components/dashboard/TaskCard";
import PointsHeader from "@/components/dashboard/PointsHeader";
import TaskModal from "@/components/dashboard/TaskModal";
import ManualReviewSubmissionModal from "@/components/dashboard/ManualReviewSubmissionModal";
import ReferralTaskModal from "@/components/dashboard/ReferralTaskModal";
import CampaignStatusBanner from "@/components/dashboard/CampaignStatusBanner";
import OnboardingOverlay from "@/components/onboarding/OnboardingOverlay";
import { useTheme } from "@/hooks/useTheme";
import { useAuth } from "@/lib/AuthContext";
import AppHeader from "@/components/layout/AppHeader";
import { isManualReviewTask, getTaskStatus } from "@/lib/taskUtils";

const CAMPAIGN_START = new Date("2026-03-17T00:00:00+03:00");

export default function Dashboard() {
  const [selectedTask, setSelectedTask] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [optimisticStatuses, setOptimisticStatuses] = useState({});
  const { isAuthenticated, isLoadingAuth, logout } = useAuth();

  const { data, isLoading: loading, error: queryError } = useDashboard();
  const { themeId, themeVars, onboardingDone, applyTheme, markDone } = useTheme(data?.user);
  const textPrimary = themeVars.textPrimary || "#ffffff";
  const textSecondary = themeVars.textSecondary || "rgba(255,255,255,0.4)";
  const completeTask = useCompleteTask();
  const upload = useUpload();

  const error = queryError ? ('Failed to load dashboard: ' + queryError.message) : null;

  const handleTaskSelect = (task) => {
    if (getTaskStatus(task, optimisticStatuses) === "pending_review") return;
    if (task.task_type === "referral") {
      setSelectedTask(task);
      return;
    }
    setSelectedTask(task);
  };

  const handleComplete = async (task, submissionData) => {
    setCompleting(true);
    try {
      const res = await completeTask.mutateAsync({
        taskId: task.id,
        submission_data: submissionData,
      });
      setSelectedTask(null);

      const pts = res.points_earned;
      setSuccessMsg(`+${pts} נקודות נוספו! 🎉`);
      setTimeout(() => setSuccessMsg(null), 3500);

      const count = pts >= 200 ? 200 : pts >= 50 ? 120 : 60;
      confetti({ particleCount: count, spread: 80, origin: { y: 0.5 }, colors: ["#c8f135", "#ffffff", "#ff3dac", "#3dffea"] });
    } catch (err) {
      console.error('Task completion failed:', err);
    } finally {
      setCompleting(false);
    }
  };

  const handleReviewSubmit = async (task, payload) => {
    setCompleting(true);
    setOptimisticStatuses((prev) => ({ ...prev, [task.id]: "pending_review" }));
    setSelectedTask(null);
    setSuccessMsg("המשימה נשלחה לבדיקה");
    setTimeout(() => setSuccessMsg(null), 3500);
    try {
      const uploadRes = await upload.mutateAsync(payload.file);
      await completeTask.mutateAsync({
        taskId: task.id,
        reference_text: payload.reference_text,
        proof_url: uploadRes.url,
      });
    } catch (err) {
      console.error('Review submission failed:', err);
    } finally {
      setCompleting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0a0a" }}>
      <div className="w-10 h-10 border-4 border-transparent rounded-full animate-spin" style={{ borderTopColor: "#c8f135" }} />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0a0a" }}>
      <p className="text-red-400 font-bold">{error}</p>
    </div>
  );

  const { contest: campaign, tasks = [], user, max_points, campaign_ended } = data || {};
  const campaignNotStarted = new Date() < CAMPAIGN_START;
  const pendingTasks = tasks.filter((t) => !t.is_completed && t.task_type !== "referral");
  const completedTasks = tasks.filter((t) => t.is_completed);
  const referralTask = tasks.find((t) => t.task_type === "referral");
  const selectedTaskStatus = selectedTask ? getTaskStatus(selectedTask, optimisticStatuses) : null;

  return (
    <div className="min-h-screen" style={{ background: themeVars.bg, fontFamily: "'Heebo', sans-serif" }}>
      <AppHeader />
      {onboardingDone === false && (
        <OnboardingOverlay
          themeId={themeId}
          onThemeChange={applyTheme}
          onComplete={markDone}
        />
      )}

      <div className="max-w-2xl mx-auto px-4 py-8 pt-[70px]" dir="rtl">
        <CampaignStatusBanner themeVars={themeVars} />
        <div data-onboard="points-header">
          <PointsHeader user={user} maxPoints={max_points} campaign={campaign} themeVars={themeVars} />
        </div>

        {referralTask && (
          <div className="mb-8">
            <TaskCard
              task={referralTask}
              index={0}
              status="open"
              onSelect={() => setSelectedTask(referralTask)}
              themeVars={themeVars}
            />
          </div>
        )}

        <AnimatePresence>
          {successMsg && (
            <motion.div
              key="toast"
              initial={{ opacity: 0, scale: 0.85, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: -8 }}
              className="text-center mb-6 py-3 px-6 rounded-full font-black text-sm w-full"
              style={{ background: "rgba(200,241,53,0.15)", color: "#c8f135", border: "1px solid rgba(200,241,53,0.4)" }}
            >
              {successMsg}
            </motion.div>
          )}
        </AnimatePresence>

        {!campaign_ended && !campaignNotStarted && pendingTasks.length > 0 && (
          <div className="mb-8" data-onboard="task-list">
            <p className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: textSecondary }}>
              משימות פתוחות
            </p>
            <div className="space-y-3">
              {pendingTasks.map((task, i) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={i}
                  status={getTaskStatus(task, optimisticStatuses)}
                  onSelect={() => handleTaskSelect(task)}
                  themeVars={themeVars}
                />
              ))}
            </div>
          </div>
        )}

        {campaignNotStarted && tasks.length > 0 && (
          <div className="mb-8">
            <p className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: textSecondary }}>
              המשימות שיחכו לך ב-22.3 👀
            </p>
            <div className="space-y-3 opacity-50 pointer-events-none select-none">
              {tasks.map((task, i) => (
                <TaskCard key={task.id} task={task} index={i} status={getTaskStatus(task, optimisticStatuses)} themeVars={themeVars} />
              ))}
            </div>
          </div>
        )}

        {completedTasks.length > 0 && !campaignNotStarted && (
          <div>
            <p className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: textSecondary }}>
              הושלמו ✓
            </p>
            <div className="space-y-3">
              {completedTasks.map((task, i) => (
                <TaskCard key={task.id} task={task} index={i} completed status="completed" themeVars={themeVars} />
              ))}
            </div>
          </div>
        )}

        {campaign_ended && (
          <div className="text-center py-12 rounded-2xl" style={{ background: "#111", border: "1px solid #222" }}>
            <p className="text-4xl mb-3">🏁</p>
            <p className="font-black text-xl text-white mb-1">הקמפיין הסתיים</p>
            <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.4)" }}>תוצאות הלידרבורד הסופיות</p>
            <Link
              to="/leaderboard"
              className="inline-block font-black text-sm px-6 py-3 rounded-full"
              style={{ background: "#c8f135", color: "#000" }}
            >
              ראה לידרבורד סופי →
            </Link>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedTask && selectedTaskStatus !== "pending_review" && (
          selectedTask.task_type === "referral" ? (
            <ReferralTaskModal
              key={selectedTask.id}
              task={selectedTask}
              referralToken={user.referral_code}
              onClose={() => setSelectedTask(null)}
            />
          ) : isManualReviewTask(selectedTask) ? (
            <ManualReviewSubmissionModal
              key={selectedTask.id}
              task={selectedTask}
              submitting={completing}
              onClose={() => setSelectedTask(null)}
              onSubmit={handleReviewSubmit}
            />
          ) : (
            <TaskModal
              key={selectedTask.id}
              task={selectedTask}
              completing={completing}
              onClose={() => setSelectedTask(null)}
              onComplete={handleComplete}
            />
          )
        )}
      </AnimatePresence>
    </div>
  );
}